import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  gitBranches,
  gitCheckout,
  gitCommit,
  gitDiff,
  gitDiscard,
  gitFetch,
  gitLog,
  gitPull,
  gitPush,
  gitShow,
  gitStage,
  gitStash,
  gitStatus,
  gitUnstage,
  type GitDiscardResult,
  type GitResult,
  type GitScope,
} from '../transport/api'
import { ApiError } from '../transport/http'
import { Icon } from '../ui/Icon'
import { GitDiff } from './GitDiff'
import { DiffSurface, FileRow, Group } from './rows'
import { BranchPicker } from './BranchPicker'
import { DiscardConfirm } from './DiscardConfirm'
import { baseName, groups, isDir, type Row } from './status'

/**
 * Source Control — a mirror of VS Code's SCM view (owner: "把 vscode 抄過來").
 *
 * The structure is VS Code's, not the old panel's: a commit message box on top with a
 * split Commit button, then collapsible groups in VS Code's order (Merge Changes,
 * Staged Changes, Changes), then a status bar carrying the branch and the sync
 * indicator. Per-row hover actions are VS Code's too — stage/unstage on each file and
 * on each group header.
 *
 * Discard is the one genuinely destructive action here. It is IRREVERSIBLE and means
 * two different things (restore a tracked file from the index, DELETE an untracked
 * one), so it always goes through an explicit confirmation naming the files and which
 * of the two will happen — and the result reports what the server actually did rather
 * than assuming the request matched the outcome.
 */

/** How far the commit box may auto-grow before it scrolls internally. */
const MSG_MAX_PX = 420

/* ── status letters + grouping: moved to status.ts (M5) so the decisions are
      directly testable instead of reachable only through this mounted panel. ── */

/* ── one file row ─────────────────────────────────────────────────────────── */

export function GitPanel({
  session,
  host,
  folder,
}: {
  /** The agent whose machine the repo lives on — scopes every git call. */
  session: string | null
  host: string | null
  /**
   * Resolve the repo from THIS directory. REQUIRED.
   *
   * Set by the file browser, whose path bar is then the repo selector: walk into a
   * checkout and Source Control is that checkout's. It was optional, with a stored pin
   * as the fallback for a standalone panel — but nothing ever mounted that panel, and
   * FileBrowser renders this only when its folder resolves, so the fallback was
   * unreachable. Required now, so the "two things choosing the repo" state the pin's own
   * comment warned about cannot be re-created by a caller omitting an argument.
   */
  folder: string
}) {
  const qc = useQueryClient()
  /*
   * THE FOLDER IS THE QUESTION. When the file browser drives this panel — and it is the
   * only thing that mounts it — the directory on screen IS what is being asked about.
   *
   * There was a PIN beside it: a `wt_git_repo:<host>:<session>` localStorage entry that
   * remembered a repo root per agent, plus an "Open a repository" field, a "Re-detect
   * Repository" menu item, and an effect that wrote the pin as soon as the server
   * resolved a root. All of it was unreachable — FileBrowser renders this only when
   * `gitFolder` is truthy and always passes `embedded`, so `folder` was never absent —
   * and it was still WRITING a localStorage key per agent on every repo resolution.
   * Deleted 2026-09-02. The bug the pin was rewritten for is worth keeping in view: a
   * per-HOST version pinned once and then showed that one repo for every agent forever,
   * which reads as "the Git panel never updates" with nothing on screen to give it away.
   */
  const root = folder
  const scope: GitScope = { path: root, session, host }
  const key = [root, session, host]

  const [msg, setMsg] = useState('')
  const [sel, setSel] = useState<{ path: string; kind: Row['kind'] } | null>(null)
  const [openGroups, setOpenGroups] = useState({ conflicts: true, staged: true, changes: true })
  const [branchOpen, setBranchOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [histOpen, setHistOpen] = useState(false)
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null)
  const [amend, setAmend] = useState(false)
  /** Files awaiting discard confirmation; null = no prompt open. */
  const [confirmDiscard, setConfirmDiscard] = useState<Row[] | null>(null)
  const [openCommit, setOpenCommit] = useState<string | null>(null)

  const statusQ = useQuery({
    queryKey: ['git-status', ...key],
    queryFn: () => gitStatus(scope),
  })
  const st = statusQ.data

  const refresh = () => void qc.invalidateQueries({ queryKey: ['git-status', ...key] })

  /**
   * Every action reports git's own output verbatim — for git that text IS the
   * diagnostic ("Everything up-to-date", a rejected non-fast-forward, a merge
   * conflict list), and paraphrasing it would throw away the only useful part.
   */
  const report = {
    onSuccess: (r: GitResult) => {
      setNote({ ok: r.ok, text: r.output || (r.ok ? 'done' : 'failed') })
      refresh()
    },
    onError: (e: unknown) => setNote({ ok: false, text: String(e) }),
  }

  const stage = useMutation({ mutationFn: (files?: string[]) => gitStage(scope, files), ...report })
  const unstage = useMutation({
    mutationFn: (files?: string[]) => gitUnstage(scope, files),
    ...report,
  })
  const commit = useMutation({
    mutationFn: (v: { message: string; push: boolean; amend: boolean }) =>
      gitCommit(scope, v.message, v.push, v.amend),
    ...report,
  })
  const push = useMutation({ mutationFn: () => gitPush(scope), ...report })
  const pull = useMutation({ mutationFn: () => gitPull(scope), ...report })
  const fetchAll = useMutation({ mutationFn: () => gitFetch(scope), ...report })
  const checkout = useMutation({
    mutationFn: (v: { branch: string; create: boolean }) => gitCheckout(scope, v.branch, v.create),
    ...report,
  })
  const stash = useMutation({
    mutationFn: (v: { action: 'push' | 'pop'; untracked?: boolean }) =>
      gitStash(scope, v.action, v.untracked ? { untracked: true } : {}),
    ...report,
  })
  /**
   * Discard reports per path, so say what actually happened instead of "done": a file
   * that was skipped (and why) is the case the user most needs to see.
   */
  const discard = useMutation({
    mutationFn: (files: string[]) => gitDiscard(scope, files),
    onSuccess: (r: GitDiscardResult) => {
      const parts = [
        r.restored?.length ? `restored ${r.restored.length}` : '',
        r.deleted?.length ? `deleted ${r.deleted.length}` : '',
        ...(r.skipped ?? []).map((s2) => `skipped ${s2.path}: ${s2.reason}`),
      ].filter(Boolean)
      setNote({ ok: r.ok, text: parts.join('\n') || r.output || 'nothing to discard' })
      setSel(null)
      refresh()
    },
    onError: (e: unknown) => setNote({ ok: false, text: String(e) }),
  })

  const busy =
    stage.isPending || unstage.isPending || commit.isPending || push.isPending ||
    pull.isPending || fetchAll.isPending || checkout.isPending ||
    discard.isPending || stash.isPending

  const g = useMemo(
    () => (st && !st.error && st.repo ? groups(st) : { conflicts: [], staged: [], changes: [] }),
    [st],
  )

  const diffQ = useQuery({
    queryKey: ['git-diff', ...key, sel?.path, sel?.kind],
    queryFn: () => gitDiff(scope, sel!.path, sel!.kind),
    // A collapsed untracked DIRECTORY has no blob and no file to diff — asking the
    // server for one just produces a git error.
    enabled: !!sel && !isDir(sel.path),
  })

  const logQ = useQuery({
    queryKey: ['git-log', ...key],
    queryFn: () => gitLog(scope, undefined, 50),
    enabled: histOpen,
  })

  const showQ = useQuery({
    queryKey: ['git-show', ...key, openCommit],
    queryFn: () => gitShow(scope, openCommit!),
    enabled: !!openCommit,
  })

  const branchQ = useQuery({
    queryKey: ['git-branches', ...key],
    queryFn: () => gitBranches(scope),
    enabled: branchOpen,
  })

  // The commit box owns Cmd/Ctrl+Enter while focused. Without stopPropagation the
  // app-wide Cmd+Enter (send to terminal / chat) would also fire on the same press.
  const onMsgKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return
    e.preventDefault()
    e.stopPropagation()
    if (msg.trim()) commit.mutate({ message: msg.trim(), push: false, amend })
  }
  useEffect(() => {
    if (commit.isSuccess && commit.data?.ok) {
      setMsg('')
      setAmend(false)
    }
  }, [commit.isSuccess, commit.data?.ok])

  // Amend is valid with an empty box (the server maps that to --no-edit) and with
  // nothing staged (rewording the last commit), so it relaxes both preconditions.
  const canCommit = amend ? !busy : msg.trim().length > 0 && g.staged.length > 0 && !busy
  const box = useRef<HTMLTextAreaElement>(null)
  /**
   * Auto-grow the message box. The FLOOR is CSS (#gitMsg min-height, four lines) —
   * not the `rows` attribute, which this inline height would override on first
   * render. The ceiling is here because the box shares the panel with the file list:
   * a message long enough to hit it scrolls instead of squeezing the list away.
   */
  useEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MSG_MAX_PX)}px`
  }, [msg])

  /* ── states with no repo ───────────────────────────────────────────────── */
  const noRepo = st && (!st.repo || st.error)

  /*
   * Embedded, the panel is a REGION inside the file browser's drawer, not a landmark
   * of its own: `aside` here would announce a second complementary landmark nested in
   * the first, and the drawer already carries the "File browser" label.
   */
  return (
    <div
      className={`git-embed${sel ? ' split' : ''}`}
      role="region"
      aria-label="Source Control"
    >
      <div id="gitHead">
        <span id="gitTitle">Source Control</span>
        {/* WHICH repo, always on screen. The pin bug hid behind its absence: the
            panel can be on a different repo than the agent, and without the name
            that state is indistinguishable from "no changes anywhere".
            The RESOLVED root first: browsing to a subdirectory of a checkout asks
            about that subdirectory but answers about the repo, and naming the folder
            would make a repo-wide change list look like it belonged to one folder. */}
        {(st?.repo ?? root) && (
          <span id="gitRepoName" title={st?.repo ?? root ?? ''}>
            {(st?.repo ?? root ?? '').split('/').filter(Boolean).pop()}
          </span>
        )}
        <button type="button" onClick={refresh} title="Refresh" aria-label="Refresh">
          <Icon name="refresh" size={16} />
        </button>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          title="Views and More Actions…"
          aria-label="More actions"
          aria-expanded={menuOpen}
        >
          <Icon name="more_horiz" size={16} />
        </button>
        {/*
          * Embedded, the drawer's own × is two centimetres above this one and closes
          * something else — this would only return to the listing. Two × side by side
          * meaning different things is a coin flip, and the lit "Git" button in the
          * footer already says how to come back.
          */}
      </div>

      {menuOpen && (
        <div className="gitmenu" role="menu">
          {[
            { label: 'Pull', run: () => pull.mutate() },
            { label: 'Push', run: () => push.mutate() },
            { label: 'Fetch (prune)', run: () => fetchAll.mutate() },
            { label: 'Stash Changes', run: () => stash.mutate({ action: 'push', untracked: true }) },
            { label: 'Pop Latest Stash', run: () => stash.mutate({ action: 'pop' }) },
            { label: 'Checkout to…', run: () => setBranchOpen(true) },
            { label: histOpen ? 'Hide History' : 'Show History', run: () => setHistOpen((v) => !v) },
          ].map((m) => (
            <button
              key={m.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false)
                m.run()
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {/* A failed status poll must never impersonate a clean tree. Without this,
          st stays undefined, the groups render empty, and a locked vault or a dead
          server reads as "no changes" — the panel's whole job, inverted. */}
      {statusQ.isError && (
        <div className="gitempty gitstatus-err">
          <p>
            {statusQ.error instanceof ApiError && statusQ.error.status === 401
              ? 'Vault locked — unlock to see changes.'
              : `couldn’t read status: ${String(statusQ.error)}`}
          </p>
        </div>
      )}

      {noRepo ? (
        <div className="gitempty">
          <p>{st?.error ?? 'not a git repository'}</p>
          {st?.start && <code>{st.start}</code>}
          {/* Folder-driven, "open a repository" is the path bar directly above: walk
              somewhere else and this answers about there. A second way to say the same
              thing would let the two disagree, which is the bug this whole prop exists
              to avoid. */}
          <p className="gitempty-hint">Browse to a checkout to see its changes.</p>
        </div>
      ) : (
        <>
          <div id="gitCommit">
            <textarea
              ref={box}
              id="gitMsg"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={onMsgKey}
              rows={4}
              placeholder={`Message (${
                navigator.platform.startsWith('Mac') ? '⌘' : 'Ctrl+'
              }Enter to commit on '${st?.branch ?? '…'}')`}
              spellCheck={false}
            />
            <div id="gitCommitRow">
              <button
                type="button"
                className="git-primary"
                disabled={!canCommit}
                onClick={() => commit.mutate({ message: msg.trim(), push: false, amend })}
                title={
                  g.staged.length === 0
                    ? 'Stage changes first — git commit only records the staged set'
                    : 'Commit'
                }
              >
                <Icon name="check" size={15} />
                {commit.isPending ? 'Committing…' : 'Commit'}
              </button>
              <button
                type="button"
                className="git-secondary"
                disabled={!canCommit}
                onClick={() => commit.mutate({ message: msg.trim(), push: true, amend })}
                title="Commit and push in one round trip"
              >
                <Icon name="arrow_upward" size={14} />
                Commit &amp; Push
              </button>
            </div>
            <label className="gitamend" title="Rewrite the previous commit instead of adding one">
              <input type="checkbox" checked={amend} onChange={(e) => setAmend(e.target.checked)} />
              <span>Amend last commit{amend && !msg.trim() ? ' (keep message)' : ''}</span>
            </label>
          </div>

          <div id="gitGroups">
            <Group
              title="Merge Changes"
              rows={g.conflicts.length}
              open={openGroups.conflicts}
              onToggle={() => setOpenGroups((v) => ({ ...v, conflicts: !v.conflicts }))}
              actions={
                <button
                  type="button"
                  onClick={() => stage.mutate(g.conflicts.map((r) => r.path))}
                  title="Stage All Merge Changes — marks these resolved"
                  aria-label="Stage all merge changes"
                >
                  <Icon name="add" size={15} />
                </button>
              }
            >
              {g.conflicts.map((r) => (
                <FileRow
                  key={r.path}
                  row={r}
                  selected={sel?.path === r.path}
                  onOpen={() => setSel({ path: r.path, kind: r.kind })}
                  onStage={() => stage.mutate([r.path])}
                />
              ))}
            </Group>

            <Group
              title="Staged Changes"
              rows={g.staged.length}
              open={openGroups.staged}
              onToggle={() => setOpenGroups((v) => ({ ...v, staged: !v.staged }))}
              actions={
                <button
                  type="button"
                  onClick={() => unstage.mutate(undefined)}
                  title="Unstage All Changes"
                  aria-label="Unstage all changes"
                >
                  <Icon name="remove" size={15} />
                </button>
              }
            >
              {g.staged.map((r) => (
                <FileRow
                  key={r.path}
                  row={r}
                  selected={sel?.path === r.path && sel.kind === 'staged'}
                  onOpen={() => setSel({ path: r.path, kind: 'staged' })}
                  onUnstage={() => unstage.mutate([r.path])}
                />
              ))}
            </Group>

            <Group
              title="Changes"
              rows={g.changes.length}
              open={openGroups.changes}
              onToggle={() => setOpenGroups((v) => ({ ...v, changes: !v.changes }))}
              actions={
                <>
                  <button
                    type="button"
                    onClick={() => setConfirmDiscard(g.changes)}
                    title="Discard All Changes"
                    aria-label="Discard all changes"
                  >
                    <Icon name="undo" size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => stage.mutate(undefined)}
                    title="Stage All Changes"
                    aria-label="Stage all changes"
                  >
                    <Icon name="add" size={15} />
                  </button>
                </>
              }
            >
              {g.changes.map((r) => (
                <FileRow
                  key={r.path}
                  row={r}
                  selected={sel?.path === r.path && sel.kind !== 'staged'}
                  onOpen={() => setSel({ path: r.path, kind: r.kind })}
                  onStage={() => stage.mutate([r.path])}
                  onDiscard={() => setConfirmDiscard([r])}
                />
              ))}
            </Group>

            {g.conflicts.length + g.staged.length + g.changes.length === 0 && (
              <div className="gitclean">No changes — the working tree is clean.</div>
            )}

            {histOpen && (
              <section className="gitgrp">
                <div className="gitgrp-hd">
                  <button type="button" className="gitgrp-tog" onClick={() => setHistOpen(false)}>
                    <Icon name="expand_more" size={16} />
                    <span className="gitgrp-title">History</span>
                  </button>
                </div>
                <div className="gitgrp-rows">
                  {logQ.data?.commits?.map((c) => (
                    <button
                      type="button"
                      className={`gitcommit${openCommit === c.hash ? ' on' : ''}`}
                      key={c.hash}
                      title={c.hash}
                      onClick={() => {
                        setSel(null)
                        setOpenCommit(c.hash)
                      }}
                    >
                      <span className="gitcommit-sub">{c.subject}</span>
                      <span className="gitcommit-meta">
                        {c.short} · {c.author} · {c.date}
                      </span>
                    </button>
                  )) ?? <div className="gitclean">loading…</div>}
                </div>
              </section>
            )}
          </div>

          {note && (
            <div className={`gitnote${note.ok ? '' : ' bad'}`} role="status">
              {!note.ok && <Icon name="warning" size={14} />}
              <pre>{note.text}</pre>
              <button type="button" onClick={() => setNote(null)} aria-label="Dismiss">
                <Icon name="close" size={12} />
              </button>
            </div>
          )}

          <div id="gitBar">
            <button
              type="button"
              className="gitbar-branch"
              onClick={() => setBranchOpen(true)}
              title="Checkout to…"
            >
              <Icon name="call_split" size={14} />
              {st?.detached ? 'HEAD (detached)' : (st?.branch ?? '…')}
            </button>
            <button
              type="button"
              className="gitbar-sync"
              onClick={() => (st?.behind ? pull : push).mutate()}
              disabled={busy || st?.detached}
              title={
                st?.upstream
                  ? `Sync with ${st.upstream}${st.behind ? ` — pull ${st.behind}` : ''}${
                      st.ahead ? ` — push ${st.ahead}` : ''
                    }`
                  : 'Publish this branch to origin'
              }
            >
              <Icon name="sync" size={13} />
              {st?.upstream ? (
                <>
                  {st.behind > 0 && (
                    <span>
                      {st.behind}
                      <Icon name="arrow_downward" size={11} />
                    </span>
                  )}
                  {st.ahead > 0 && (
                    <span>
                      {st.ahead}
                      <Icon name="arrow_upward" size={11} />
                    </span>
                  )}
                  {st.ahead === 0 && st.behind === 0 && <span>up to date</span>}
                </>
              ) : (
                <span>publish</span>
              )}
            </button>
          </div>
        </>
      )}

      {sel && (
        <DiffSurface>
        <div id="gitDiffPane">
          <div id="gitDiffHead">
            <span className="gd-path" title={sel.path}>
              {baseName(sel.path)}
            </span>
            <span className="gd-kind">
              {sel.kind === 'staged' ? 'staged' : sel.kind === 'untracked' ? 'untracked' : 'working tree'}
            </span>
            <button type="button" onClick={() => setSel(null)} aria-label="Close diff">
              <Icon name="close" size={14} />
            </button>
          </div>
          {isDir(sel.path) ? (
            <div className="gd-empty">
              git collapses a wholly-untracked folder into one entry, so there is nothing to
              diff yet. Stage it and its files appear individually.
            </div>
          ) : diffQ.isPending ? (
            <div className="gd-empty">loading diff…</div>
          ) : diffQ.error ? (
            <div className="gd-empty">{String(diffQ.error)}</div>
          ) : (
            <GitDiff diff={diffQ.data?.diff ?? ''} empty="No textual changes (mode or binary)." />
          )}
        </div>
        </DiffSurface>
      )}

      {openCommit && (
        <DiffSurface>
        <div id="gitDiffPane">
          <div id="gitDiffHead">
            <span className="gd-path">{showQ.data?.short ?? openCommit.slice(0, 7)}</span>
            <span className="gd-kind">{showQ.data?.subject ?? 'commit'}</span>
            <button type="button" onClick={() => setOpenCommit(null)} aria-label="Close commit">
              <Icon name="close" size={14} />
            </button>
          </div>
          {showQ.isPending ? (
            <div className="gd-empty">loading commit…</div>
          ) : showQ.error ? (
            <div className="gd-empty">{String(showQ.error)}</div>
          ) : (
            <>
              <div className="gitcommit-hdr">
                <strong>{showQ.data?.subject}</strong>
                <span>
                  {showQ.data?.author} · {showQ.data?.date} · {showQ.data?.short}
                </span>
                {showQ.data?.body?.trim() && <pre>{showQ.data.body.trim()}</pre>}
              </div>
              <GitDiff diff={showQ.data?.diff ?? ''} empty="This commit has no diff." />
            </>
          )}
        </div>
        </DiffSurface>
      )}

      {confirmDiscard && (
        <DiscardConfirm
          rows={confirmDiscard}
          host={host}
          repo={st?.repo ?? null}
          busy={discard.isPending}
          onCancel={() => setConfirmDiscard(null)}
          onConfirm={() => {
            discard.mutate(confirmDiscard.map((r) => r.path))
            setConfirmDiscard(null)
          }}
        />
      )}

      {branchOpen && (
        <BranchPicker
          current={st?.branch ?? null}
          branches={branchQ.data?.branches ?? []}
          loading={branchQ.isPending}
          busy={checkout.isPending}
          onPick={(branch, create) => {
            checkout.mutate({ branch, create })
            setBranchOpen(false)
          }}
          onClose={() => setBranchOpen(false)}
        />
      )}
    </div>
  )
}

/* ── branch quick-pick (VS Code's "Checkout to…") ─────────────────────────── */

